import { Box, IconButton, useTheme } from '@mui/material';
import { EffectCoverflow, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ALLOW_EXPERIENCE_DETAILS_PREFETCH } from '@/constants/prefetch';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ExperienceModel } from '@/logic/services/experiences';
import { FC } from 'react';
import { ILocale } from '@/lang/types';

import Link from 'next/link';
import { checkIsRtl } from '@/lang/utils';
import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';
import { useRouter } from 'next/dist/client/router';
import ImageBoxFuture from '../shared/image-box-future';

interface experiencesProps {
  experiences: ExperienceModel[];
}

export const ExperiencesSlider: FC<experiencesProps> = experiencesProps => {
  const theme = useTheme();
  const { locale } = useRouter();
  const isRtl = checkIsRtl(locale as ILocale);
  const { cityId: browserCityId } = useCity();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        component='div'
        sx={{
          overflow: 'visible',
          '.swiper': {
            width: { tablet: '165vw', mobile: '200vw' },
            pt: pxToRem(32),
            overflow: 'visible',
          },

          '& .swiper-slide.swiper-slide-active  div.MuiBox-root ': {
            '& img': {
              filter:
                'drop-shadow(4px 0px 18px rgba(0, 0, 0, 0.3)) drop-shadow(-4px 0px 18px rgba(0, 0, 0, 0.3));',
            },
            '& a': { display: 'flex ' },
          },
          '& .swiper-slide.swiper-slide-active  div.MuiBox-root::after, .swiper-slide div.MuiBox-root div.MuiBox-root::after':
            {
              width: '0%',
            },
          '& .swiper-slide': {
            'a': { display: 'none' },
          },

          '& .swiper-pagination-bullet': {
            width: { tablet: pxToRem(12), mobile: '6px' },
            height: { tablet: pxToRem(12), mobile: '6px' },
            background: theme.palette.grey[30],
            backdropFilter: ' blur(5px)',
            borderRadius: '100%',
            mx: pxToRem(6),

            cursor: 'pointer',
          },
          '& .swiper-pagination-bullet-active': {
            background: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}`,
            width: { tablet: pxToRem(14), mobile: '8px' },
            height: { tablet: pxToRem(14), mobile: '8px' },
          },

          '& .swiper-pagination': {
            display: 'flex',
            justifyContent: 'center',
            mt: pxToRem(24),
            mb: { tablet: pxToRem(48), mobile: '16px' },
            position: 'static',
            alignItems: 'center',
          },
        }}
      >
        {experiencesProps.experiences.length !== 1 ? (
          <Swiper
            spaceBetween={0}
            slidesPerView={3}
            modules={[EffectCoverflow, Pagination]}
            pagination={{ clickable: true }}
            scrollbar={{ draggable: true }}
            centeredSlides
            slideToClickedSlide
            loop={experiencesProps.experiences.length !== 1}
            coverflowEffect={{
              depth: 0,
              rotate: 0,
              scale: 0.8,
              slideShadows: false,
            }}
            speed={600}
            effect='coverflow'
          >
            {experiencesProps.experiences.map((item: ExperienceModel) => (
              <SwiperSlide key={item.id}>
                <Box
                  key={item.id}
                  component='div'
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    '::after': {
                      content: "''",
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      background: 'rgba(0, 0, 0, 0.42)',
                      backdropFilter: ' blur(5px)',
                      left: 0,
                      borderRadius: '20px',
                      overflow: 'hidden',
                      cursor: 'grab',
                      top: 0,
                    },
                  }}
                >
                  <ImageBoxFuture
                    img={item.cover}
                    title={item?.coverAlt || item.name}
                    layout='intrinsic'
                    objectFit='cover'
                    width='1920px'
                    height='1080px'
                    sx={{
                      borderRadius: '20px',
                      overflow: 'hidden',
                    }}
                  />

                  <Box
                    component='div'
                    sx={{
                      display: 'flex',
                      position: 'absolute',
                      bottom: 0,
                      height: {
                        tablet: `${pxToRem(130)} !important`,
                        mobile: `60px !important`,
                      },
                      p: { tablet: pxToRem(32), mobile: '12px' },

                      background:
                        'linear-gradient(180deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.468) 23.23%, #000000 77.4%)',
                      borderRadius: '0px 0px 20px 20px',
                      width: ' 100%',
                      justifyContent: 'space-between',
                      alignItems: { tablet: 'flexEnd', mobile: 'flex-end' },
                    }}
                  >
                    <Link
                      href={{
                        pathname: `/experiences/${item.uniqueName}`,
                        // Omit cityId when unset instead of emitting `?cityId=`.
                        query: { ...(browserCityId && { cityId: browserCityId }) },
                      }}
                      prefetch={ALLOW_EXPERIENCE_DETAILS_PREFETCH}
                      legacyBehavior
        passHref
                    >
                      <IconButton
                        sx={{
                          width: { tablet: pxToRem(64), mobile: '34px' },
                          height: { tablet: pxToRem(64), mobile: '34px' },
                          background: theme.palette.common.white,
                          ml: 'auto',
                        }}
                      >
                        {isRtl ? <ArrowBackIosIcon /> : <ArrowForwardIosIcon />}
                      </IconButton>
                    </Link>
                  </Box>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <Box
            key={experiencesProps.experiences[0].id}
            component='div'
            sx={{
              // height: { tablet: pxToRem(610 + 50), mobile: '125px' },
              m: 'auto',
              position: 'relative',
              width: '50%',
            }}
          >
            <ImageBoxFuture
              img={experiencesProps.experiences[0]?.cover}
              title={experiencesProps.experiences[0]?.coverAlt}
              layout='intrinsic'
              objectFit='cover'
              width='1920px'
              height='1080px'
              sx={{
                // maxWidth: '100%',
                // width: '100%',
                // height: '100%',
                // objectFit: 'cover',
                borderRadius: '20px',
                overflow: 'hidden',
                position: 'relative',
              }}
            />
            {/*<Box*/}
            {/*  component='img'*/}
            {/*  src={experiencesProps.experiences[0]?.cover}*/}
            {/*  alt={experiencesProps.experiences[0]?.coverAlt}*/}
            {/*  sx={{*/}
            {/*    maxWidth: '100%',*/}
            {/*    width: '100%',*/}
            {/*    height: '100%',*/}
            {/*    objectFit: 'cover',*/}
            {/*    borderRadius: '20px',*/}
            {/*  }}*/}
            {/*/>*/}
            {/*<Box*/}
            {/*  component='div'*/}
            {/*  sx={{*/}
            {/*    display: 'flex',*/}
            {/*    position: 'absolute',*/}
            {/*    bottom: 0,*/}
            {/*    height: `${pxToRem(130)} !important`,*/}
            {/*    p: pxToRem(32),*/}
            {/*    background:*/}
            {/*      'linear-gradient(180deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.468) 23.23%, #000000 77.4%)',*/}
            {/*    borderRadius: '0px 0px 20px 20px',*/}
            {/*    width: ' 100%',*/}
            {/*    justifyContent: 'space-between',*/}
            {/*    alignItems: 'flexEnd',*/}
            {/*    backgroundColor: 'red',*/}
            {/*  }}*/}
            {/*>*/}
            <Box
              component='div'
              sx={{
                // display: 'flex',
                position: 'absolute',
                bottom: { tablet: pxToRem(32), mobile: '12px' },
                right: { tablet: pxToRem(32), mobile: '12px' },
                // height: {
                //   tablet: `${pxToRem(130)} !important`,
                //   mobile: `60px !important`,
                // },
                // p: { tablet: pxToRem(32), mobile: '12px' },
                //
                // background:
                //   'linear-gradient(180deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.468) 23.23%, #000000 77.4%)',
                // borderRadius: '0px 0px 20px 20px',
                // width: ' 100%',
                // justifyContent: 'space-between',
                // alignItems: { tablet: 'flexEnd', mobile: 'flex-end' },
              }}
            >
              <Link
                href={{
                  pathname: `/experiences/${experiencesProps?.experiences[0]?.uniqueName}`,
                  // pathname: `/experiences/${experiencesProps?.experiences[0]?.id}`,
                  // Omit cityId when unset instead of emitting `?cityId=`.
                  query: { ...(browserCityId && { cityId: browserCityId }) },
                }}
                legacyBehavior
        passHref
                prefetch={ALLOW_EXPERIENCE_DETAILS_PREFETCH}
              >
                <IconButton
                  sx={{
                    width: { tablet: pxToRem(64), mobile: '34px' },
                    height: { tablet: pxToRem(64), mobile: '34px' },
                    background: theme.palette.common.white,
                    ml: 'auto',
                  }}
                >
                  {isRtl ? <ArrowBackIosIcon /> : <ArrowForwardIosIcon />}
                </IconButton>
              </Link>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ExperiencesSlider;
